import { Button, Input, Table } from 'antd';
import { debounce } from 'lodash';
import { Eye, Search, SquareUserRound } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import Highlighter from 'react-highlight-words';
import { useSelector } from 'react-redux';
import { getCustomers, makeContact } from '../../redux/actions/customer';
import { pageVariants } from '../../constants';
import { motion } from 'framer-motion';
import CustomerModal from '../../components/CustomerModal';
import useAuth from '../../hooks/useAuth';

const CustomersScreen = () => {
    const { user: { role } } = useAuth();
    const { customers, customersPagination: pagination } = useSelector(
        (state) => state.customersSlice
    );
    const [search, setSearch] = useState('');
    const [customerData, setCustomerData] = useState();

    const renderHighlightedText = (text) =>
        search ? (
            <Highlighter
                highlightClassName="highlighted-text"
                searchWords={[search]}
                autoEscape
                textToHighlight={text?.toString() || ''}
            />
        ) : (
            text
        );

    const columns = [
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
            width: 100,
            render: (text) => renderHighlightedText(text),
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text) => renderHighlightedText(text),
        },
        {
            title: 'Sales Rep.',
            dataIndex: 'salesrep',
            key: 'salesrep',
            width: 200,
            render: (text) => renderHighlightedText(text),
        },
        {
            title: 'Credit Controller',
            dataIndex: 'credit_controller',
            key: 'credit_controller',
            width: 200,
            render: (text) => renderHighlightedText(text),
        },
        {
            title: 'Phone',
            dataIndex: 'phone',
            key: 'phone',
            width: 200,
            render: (text) => renderHighlightedText(text),
        },
        {
            title: 'WhatsApp Number',
            dataIndex: 'invoice_whatsapp_number',
            key: 'invoice_whatsapp_number',
            width: 200,
            render: (text) => renderHighlightedText(text),
        },
        {
            title: 'Assigned User',
            dataIndex: 'assigned_user_name',
            key: 'assigned_user_name',
            width: 200,
        },
        {
            title: 'Actions',
            key: 'actions',
            dataIndex: 'in_contact',
            align: 'end',
            width: 200,
            hidden: role === 2,
            render: (_, record) =>
                record?.phone &&
                (!record?.in_contact ? (
                    <Button
                        size="small"
                        icon={<SquareUserRound size={14} cursor="pointer" />}
                        color="primary"
                        variant="outlined"
                        onClick={async () => {
                            await makeContact({
                                customer_id: record?.id,
                            });
                            getCustomers();
                        }}
                    >
                        Make Contact
                    </Button>
                ) : (
                    <Button
                        size="small"
                        icon={<Eye size={14} cursor="pointer" />}
                        color="primary"
                        variant="outlined"
                        onClick={() => {
                            setCustomerData(record);
                        }}
                    >
                        View
                    </Button>
                )),
        },
    ];

    const fetchCustomers = useCallback(
        debounce((searchValue) => {
            const query = { search: searchValue };
            getCustomers(query);
        }, 500),
        []
    );

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearch(value);
        fetchCustomers(value);
    };

    const handlePaginationChange = async (page) => {
        await getCustomers({ page, search });
    };

    useEffect(() => {
        getCustomers();
    }, []);

    return (
        <motion.div
            className="Customers"
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={pageVariants}
        >
            <div className="header-flex">
                <div className="page-title">Customers</div>
            </div>

            <div className="theme-bg shadow">
                <motion.div
                    className="filter-header p-6"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0, transition: { duration: 0.3 } }}
                >
                    <Input
                        placeholder="Search..."
                        size="large"
                        allowClear
                        prefix={<Search size={18} color="rgb(156,163,175)" />}
                        value={search}
                        onChange={handleSearch}
                    />
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0, transition: { duration: 0.4 } }}
                >
                    <Table
                        columns={columns}
                        dataSource={customers}
                        rowKey="id"
                        components={{
                            header: {
                                cell: (props) => <th {...props} className="table-header px-2" />,
                            },
                            body: {
                                cell: (props) => <td {...props} className="theme-bg px-2 py-1" />,
                            },
                        }}
                        pagination={{
                            current: pagination?.page,
                            pageSize: pagination?.limit,
                            total: pagination?.total,
                            showSizeChanger: false,
                            onChange: handlePaginationChange,
                            position: ['bottomCenter'],
                        }}
                        scroll={{ x: 'max-content', y: 'calc(100dvh - 360px)' }}
                    />
                </motion.div>
                <CustomerModal open={customerData} data={customerData} onCancel={() => setCustomerData()} />
            </div>
        </motion.div>
    );
};

export default CustomersScreen;
